import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RotateCcw, Eye } from "lucide-react";
import { api } from "@/lib/api";
import { FitVideo, RankBadge } from "@/components/landing/ClipVisuals";
import { useI18n } from "@/context/I18nContext";
import { PhoneClip } from "@/components/landing/PhoneClip";
import { SectionHead } from "@/components/landing/HowItWorks";
import { EXAMPLES } from "@/components/landing/data";

const ORDER = { "1:1": "sm:order-1", "9:16": "sm:order-2", "16:9": "sm:order-3" };

const Example = ({ ex, i }) => {
  const { t } = useI18n();
  const [k, setK] = useState(0);
  return (
    <div className={`space-y-4 self-center ${ORDER[ex.format] || ""}`} data-testid={`clip-card-${i}`}>
      <div className="cx-clip-hover"><PhoneClip key={k} ex={ex} title={t(ex.key)} testid={`clip-phone-${i}`} format={ex.format} /></div>
      <button onClick={() => setK((x) => x + 1)} data-testid={`clip-play-btn-${i}`} className="mx-auto cx-ghost rounded-full px-4 py-2 text-xs text-slate-300 flex items-center gap-2">
        <RotateCcw className="w-3.5 h-3.5 text-[#3ad7ff]" />{t("ld.ex_play")}
      </button>
    </div>
  );
};

const GalleryClip = ({ g, i }) => {
  const { t } = useI18n();
  return (
    <div className={`space-y-4 ${i === 1 ? "lg:-translate-y-10" : ""}`} data-testid={`gallery-card-${i}`}>
      <Link to={`/c/${g.share_id}`} className="block mx-auto w-[260px] rounded-[2.4rem] p-[6px] bg-gradient-to-b from-[#2f7bff]/60 via-white/10 to-[#a746ff]/60 shadow-[0_30px_80px_-20px_rgba(47,123,255,0.45)] transition-transform hover:-translate-y-1">
        <div className="relative aspect-[9/16] rounded-[2rem] overflow-hidden bg-black">
          <FitVideo sid={g.share_id} aspect={g.aspect} testid={`gallery-video-${i}`} />
          {g.badge && <div className="absolute top-4 left-4"><RankBadge badge={g.badge} testid={`gallery-badge-${i}`} /></div>}
          <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/90 to-transparent">
            <p className="text-white text-sm font-semibold line-clamp-2">{g.title}</p>
            <p className="text-[11px] text-slate-300 mt-1 flex items-center gap-2">{g.creator}<span className="flex items-center gap-1 font-mono"><Eye className="w-3 h-3" />{g.views}</span></p>
          </div>
        </div>
      </Link>
      <p className="text-center text-xs text-[#3ad7ff] font-mono">{t("ld.gal_by")} {g.creator}</p>
    </div>
  );
};

export const Examples = () => {
  const { t } = useI18n();
  const [gal, setGal] = useState([]);
  useEffect(() => { api.get("/gallery").then((r) => setGal(r.data)).catch(() => {}); }, []);
  const fill = EXAMPLES.slice(0, Math.max(0, 3 - gal.length));
  return (
    <section id="examples" className="relative py-24 scroll-mt-20" data-testid="examples-section">
      <div className="absolute inset-x-0 top-1/3 h-[420px] bg-[#a746ff]/[0.06] blur-[120px] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHead label={t("ld.ex_label")} title={gal.length ? t("ld.gal_title") : t("ld.ex_title")} sub={gal.length ? t("ld.gal_sub") : t("ld.ex_note")} />
        <div className="grid sm:grid-cols-3 gap-8 max-w-4xl mx-auto pt-6 items-center">
          {gal.slice(0, 6).map((g, i) => <div key={g.share_id} className="cx-clip-hover self-center"><GalleryClip g={g} i={i} /></div>)}
          {fill.map((ex, i) => <Example key={ex.key} ex={ex} i={gal.length + i} />)}
        </div>
        {gal.length > 0 && (
          <div className="text-center mt-14">
            <Link to="/vitrine" className="cx-ghost rounded-full px-6 py-3 text-sm text-white inline-flex items-center gap-2" data-testid="landing-vitrine-link">{t("vt.see_all")}</Link>
          </div>
        )}
      </div>
    </section>
  );
};
